/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 * */

/**
 * Temp access email modal styles
 *
 * @file tempAccessEmailModalStyles.js
 * @author Anuja Gaikwad
 * @since 2.3.4
 */

import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import mixins from '../../assets/styles/mixins.scss';
import CommonStyles from '../../assets/styles/CommonStyles';
import { createScopedClasses, createScopedStyles } from '../../assets/styles/createScopedStyles';

const instructionsScrollStyles = CommonStyles().scrollContainer({
  scrollbarWidth: 8,
  scrollbarColor: `${mixins.quaternary} transparent`,
  overflowY: 'auto'
});

const tempAccessEmailModalStyles = {
  paper: {
    borderRadius: 24,
    padding: 48,
    width: 800,
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: 'calc(100vh - 48px)',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    margin: 16,
    backgroundColor: mixins.chalkWhiteFont,
    boxShadow: `0 1px 3px 0 ${mixins.color_elevation_alpha_04}`,
    position: 'relative',

    [mixins['md-sm-maxwidth']]: {
      padding: 24,
      borderRadius: 16
    }
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    padding: 4,
    color: mixins.color_text_primary,
    height: 32,
    width: 32,
    zIndex: 1
  },
  closeIcon: {
    width: 18,
    height: 18
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    paddingRight: 32
  },
  title: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 24,
    fontWeight: mixins.fontWeight500,
    lineHeight: '32px',
    letterSpacing: '-0.48px',
    color: mixins.color_text_primary,
    margin: 0
  },
  subTitle: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize14,
    fontWeight: mixins.fontWeight400,
    lineHeight: '22px',
    color: '#5b5b5b',
    margin: 0
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden'
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  fieldLabel: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: mixins.navyColor
  },
  recipientsRow: {
    display: 'flex',
    gap: 24,
    alignItems: 'flex-end',
    justifyContent: 'flex-end'
  },
  recipientsFieldWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flex: '1 1 555px',
    maxWidth: 555,
    minWidth: 0
  },
  recipientsField: {
    width: '100%',
    boxSizing: 'border-box',
    border: `1px solid ${mixins.amethyst}`,
    borderRadius: 4,
    padding: 12,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    lineHeight: '24px',
    color: mixins.greyScaleColor,
    backgroundColor: mixins.chalkWhiteFont,
    outline: 'none',
    '&:focus': {
      borderColor: mixins.linkDefault
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed'
    }
  },
  copyButton: {
    borderRadius: 1000,
    border: `2px solid ${mixins.navyColor}`,
    color: mixins.navyColor,
    padding: '10px 20px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    textTransform: 'none',
    backgroundColor: 'transparent',
    minHeight: 40,
    minWidth: 125,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: mixins.flaggedChipColor
    },
    '& $copyButtonStartIcon > *:first-child': {
      fontSize: 16
    }
  },
  copyButtonLabel: {
    fontFamily: mixins.fontFamilyPlusSans,
    color: mixins.navyColor,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px'
  },
  copyButtonStartIcon: {
    marginLeft: 0,
    marginRight: 8
  },
  instructionsGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    width: '100%',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden'
  },
  instructionsBodyInput: {
    ...instructionsScrollStyles,
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    boxSizing: 'border-box',
    width: '100%',
    flex: '1 1 auto',
    minHeight: 200,
    border: `1px solid ${mixins.amethyst}`,
    borderRadius: 4,
    padding: 12,
    backgroundColor: mixins.chalkWhiteFont,
    overflowX: 'hidden'
  },
  instructionsBodyText: {
    flex: '1 0 0',
    width: '100%',
    minWidth: 0,
    margin: 0,
    padding: 0,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize18,
    fontWeight: mixins.fontWeight400,
    lineHeight: 1.5,
    letterSpacing: 0,
    color: '#252525',
    fontFeatureSettings: '"liga" 0',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    outline: 'none',
    textAlign: 'left'
  },
  inlineLink: {
    color: mixins.navyColor,
    textDecoration: 'underline',
    textDecorationStyle: 'solid',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize18,
    fontWeight: mixins.fontWeight400,
    lineHeight: mixins.lineHeight24,
    wordBreak: 'break-word',
    '&:hover': {
      color: mixins.navyColor,
      textDecoration: 'underline'
    },
    '&:visited': {
      color: mixins.navyColor
    },
    '&:focus-visible': {
      outline: `2px solid ${mixins.navyColor}`,
      borderRadius: 2
    }
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
    flexShrink: 0
  }
};

export const classes = createScopedClasses('aet-temp-access-email-modal', tempAccessEmailModalStyles);

export const scopedStyles = createScopedStyles(tempAccessEmailModalStyles, classes);

export const TempAccessEmailModalStylesRoot = styled(Paper)(scopedStyles);

export default tempAccessEmailModalStyles;
